import React, { useState } from 'react';
import { Linkedin, Upload, X, FileText, CheckCircle, AlertCircle } from 'lucide-react';

const ProfileImport = ({ onImport, currentData = {} }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  const handleCsvFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) {
      return;
    }

    // Check file size (5 MB = 5 * 1024 * 1024 bytes)
    const maxSize = 5 * 1024 * 1024; // 5 MB
    if (selectedFile.size > maxSize) {
      setError(`File size exceeds the 5 MB limit. Your file is ${(selectedFile.size / (1024 * 1024)).toFixed(2)} MB. Please use a smaller file.`);
      setCsvFile(null);
      setPreviewData(null);
      return;
    }

    if (selectedFile.type !== 'text/csv' && !selectedFile.name.toLowerCase().endsWith('.csv')) {
      setError('Please upload a CSV file.');
      setCsvFile(null);
      return;
    }

    // Set file and clear previous errors
    setCsvFile(selectedFile);
    setError(null);
    setLoading(true);
    setPreviewData(null);
    
    try {
      // Add a small delay to ensure UI updates
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const profileData = await extractProfileFromCSV(selectedFile);
      
      if (!profileData || !profileData.title) {
        setError('No profile data found in the CSV file. Please ensure the file contains profile information with headers: First Name, Last Name, Headline, Summary, Geo Location');
        setCsvFile(null);
        setPreviewData(null);
      } else {
        setPreviewData(profileData);
        setError(null);
      }
    } catch (err) {
      console.error('CSV parsing error:', err);
      let errorMessage = 'Failed to parse CSV file. ';
      if (err.message) {
        errorMessage = err.message;
      } else {
        errorMessage += 'Please ensure the file is a valid CSV file with the correct format.';
      }
      setError(errorMessage);
      setCsvFile(null);
      setPreviewData(null);
    } finally {
      setLoading(false);
    }
  };

  const extractProfileFromCSV = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          
          // Helper function to parse CSV line with quoted fields
          const parseCSVLine = (line) => {
            const result = [];
            let current = '';
            let inQuotes = false;
            
            for (let i = 0; i < line.length; i++) {
              const char = line[i];
              const nextChar = line[i + 1];
              
              if (char === '"' && inQuotes && nextChar === '"') {
                current += '"';
                i++; // Skip next quote
              } else if (char === '"') {
                inQuotes = !inQuotes;
              } else if (char === ',' && !inQuotes) {
                result.push(current.trim());
                current = '';
              } else {
                current += char;
              }
            }
            
            result.push(current.trim());
            return result.map(field => field.replace(/^"|"$/g, ''));
          };
          
          // Split by lines
          const lines = text.split(/\r?\n/).filter(line => line.trim().length > 0);
          
          if (lines.length < 2) {
            reject(new Error('CSV file appears to be empty or has no data rows.'));
            return;
          }
          
          // Parse header using the same parseCSVLine function
          const header = parseCSVLine(lines[0]);
          
          // Find column indices (case-insensitive)
          const firstNameIndex = header.findIndex(h => h.toLowerCase().includes('first') && h.toLowerCase().includes('name'));
          const lastNameIndex = header.findIndex(h => h.toLowerCase().includes('last') && h.toLowerCase().includes('name'));
          const headlineIndex = header.findIndex(h => h.toLowerCase() === 'headline');
          const summaryIndex = header.findIndex(h => h.toLowerCase() === 'summary');
          const geoLocationIndex = header.findIndex(h => h.toLowerCase().includes('geo') && h.toLowerCase().includes('location'));
          
          if (firstNameIndex === -1 || lastNameIndex === -1) {
            reject(new Error('CSV file must contain "First Name" and "Last Name" columns.'));
            return;
          }
          
          // Parse data row (row 2 contains the actual data)
          if (lines.length < 2) {
            reject(new Error('CSV file does not contain profile data in row 2.'));
            return;
          }
          
          const values = parseCSVLine(lines[1]);
          
          if (values.length <= Math.max(firstNameIndex, lastNameIndex)) {
            reject(new Error('CSV file does not have enough columns.'));
            return;
          }
          
          const firstName = values[firstNameIndex]?.trim() || '';
          const lastName = values[lastNameIndex]?.trim() || '';
          const headline = headlineIndex >= 0 ? values[headlineIndex]?.trim() || '' : '';
          const summary = summaryIndex >= 0 ? values[summaryIndex]?.trim() || '' : '';
          const geoLocation = geoLocationIndex >= 0 ? values[geoLocationIndex]?.trim() || '' : '';
          
          // Combine first and last name for title
          const title = [firstName, lastName].filter(Boolean).join(' ');
          
          if (!title) {
            reject(new Error('Could not extract name from CSV file.'));
            return;
          }
          
          const profileData = {
            title,
            headline,
            about: summary,
            location: geoLocation
          };
          
          resolve(profileData);
        } catch (err) {
          reject(new Error(`Failed to parse CSV file: ${err.message}`));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read CSV file'));
      };
      
      reader.readAsText(file);
    });
  };

  const handleImport = () => {
    if (previewData) {
      setLoading(true);
      try {
        onImport(previewData);
        setSuccess('Successfully imported profile information.');
        setTimeout(() => {
          setIsOpen(false);
          setCsvFile(null);
          setPreviewData(null);
          setSuccess(null);
          setError(null);
        }, 2000);
      } catch (err) {
        setError('Failed to import profile information. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setCsvFile(null);
    setPreviewData(null);
    setError(null);
    setSuccess(null);
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="btn btn-outline w-full inline-flex items-center justify-center space-x-2 mb-4"
        type="button"
      >
        <Linkedin className="w-5 h-5" />
        <span>Import Entire Basic Info/Contact Info from LinkedIn</span>
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Linkedin className="w-6 h-6 text-blue-600" />
                  <h2 className="text-2xl font-bold text-gray-900">Import Basic Info/Contact Info from LinkedIn</h2>
                </div>
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600"
                  type="button"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* CSV Upload Instructions */}
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    How to Export Your LinkedIn Data
                  </h3>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
                    <li>Go to <strong>LinkedIn Settings & Privacy</strong></li>
                    <li>Click <strong>"Data Privacy"</strong> → <strong>"Get a copy of your data"</strong></li>
                    <li>Select <strong>"Download larger data archive, including connections, verifications, contacts, account history, and information we infer about you based on your profile and activity."</strong></li>
                    <li>Click <strong>"Request archive"</strong></li>
                    <li>Download the <strong>Complete_LinkedInDataExport</strong> file when ready (usually takes a few minutes)</li>
                    <li>Upload the <strong>Profile.csv</strong> file here</li>
                  </ol>
                  <p className="text-xs text-blue-700 mt-3">
                    <strong>Note:</strong> Maximum file size is 5 MB. The system will extract your name, headline, summary, and location.
                  </p>
                </div>

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      onChange={handleCsvFileSelect}
                      className="hidden"
                      disabled={loading}
                    />
                    <span className="btn btn-primary inline-flex items-center space-x-2">
                      <Upload className="w-5 h-5" />
                      <span>{loading ? 'Processing CSV...' : 'Choose CSV File'}</span>
                    </span>
                  </label>
                  {csvFile && (
                    <p className="mt-4 text-sm text-gray-600">
                      Selected: <span className="font-semibold">{csvFile.name}</span>
                      {loading && (
                        <span className="ml-2 text-primary-600">(Extracting profile data...)</span>
                      )}
                    </p>
                  )}
                </div>

                {previewData && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <h3 className="font-semibold text-green-900">
                        Profile data ready to import
                      </h3>
                    </div>
                    <div className="space-y-2 bg-white rounded p-3 text-sm">
                      {previewData.title && (
                        <div>
                          <span className="font-semibold text-gray-700">Name:</span>{' '}
                          <span className="text-gray-900">{previewData.title}</span>
                        </div>
                      )}
                      {previewData.headline && (
                        <div>
                          <span className="font-semibold text-gray-700">Headline:</span>{' '}
                          <span className="text-gray-900">{previewData.headline}</span>
                        </div>
                      )}
                      {previewData.location && (
                        <div>
                          <span className="font-semibold text-gray-700">Location:</span>{' '}
                          <span className="text-gray-900">{previewData.location}</span>
                        </div>
                      )}
                      {previewData.about && (
                        <div>
                          <span className="font-semibold text-gray-700">About:</span>{' '}
                          <span className="text-gray-900">{previewData.about.substring(0, 200)}...</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-red-900 mb-1">Error</h3>
                    <p className="text-sm text-red-800 whitespace-pre-wrap">{error}</p>
                  </div>
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-green-900 mb-1">Success</h3>
                    <p className="text-sm text-green-800">{success}</p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={handleClose}
                  className="btn btn-outline"
                  type="button"
                  disabled={loading}
                >
                  Cancel
                </button>
                {previewData && (
                  <button
                    onClick={handleImport}
                    className="btn btn-primary"
                    disabled={!previewData || loading}
                    type="button"
                  >
                    {loading ? 'Importing...' : 'Import Profile Data'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProfileImport;
